import { useCallback, useContext, useEffect } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Alert, Anchor, Box, FieldSet } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useSetListingCriteria } from '../../../../hooks/queries/listingCriteria';
import {
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
} from '../../../../hooks/queries/multiplex';
import useMultiplexSetListingCriteria, {
  mapCriterionToMultiplexListingCriterionRequest,
} from '../../../../hooks/queries/multiplex/useMultiplexSetListingCriteria';
import useIsOnReviewStep from '../../../../hooks/useIsOnReviewStep';
import { useSelector } from '../../../../selectors/useSelector';
import listingCriteriaUtils from '../../../../utils/listingCriteriaUtils';
import wizardActions from '../../../Wizard/actions';

import { NOT_APPLICABLE } from '../../../../constants/ListingCriteriaConstants';
import { TYPES_MAP } from '../../../../constants/ListingCriteriaConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import ListingCriteria from '../../../../model/ListingCriteria';
import Criterion from '../../../../model/ListingCriteria/Criterion';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { WizardBody, WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';

import { WizardContext } from '../../../Wizard/context';
import WizardAlert from '../../../WizardAlert';
import MinCreditScoreFormFieldContainer from '../../MinCreditScoreFormFieldContainer';
import IncomeToRentRatioFormField from './IncomeToRentRatioFormField';

import type { PartialMessageProps } from '@zg-rentals/backend-styled-components';
import type { UserPropertyCriteriaSetRequestWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ErrorRMWProps } from '../../../../model/ErrorRMW';
import type { CriterionProps } from '../../../../model/ListingCriteria/Criterion';

const FormSectionContainer = () => {
  const dispatch = useDispatch();

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const isOnReviewStep = useIsOnReviewStep();

  const { data } = useMultiplexDetails({ enabled: !isOnReviewStep });

  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const {
    reducerWizardDispatch,
    // @ts-expect-error WizardContext is not typed
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { dataToBeSavedMap, isInModalState, serverErrorObj },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { currentCriteria, listingAlias } = useSelector((state) => {
    return {
      currentCriteria: state?.properties?.currentProperty?.listingCriteria?.criteria,

      listingAlias: state?.properties?.currentProperty?.listingAlias,
    };
  }, shallowEqual);

  const initialCriteria =
    isInMultiplexListingFlow && data?.listingCriteria?.criteria
      ? (data.listingCriteria.criteria as Array<CriterionProps>)
      : currentCriteria;

  const criteriaForSection = initialCriteria ?? [];

  const getCriterion = (type: string) => listingCriteriaUtils.getCriteriaByType(criteriaForSection, type);

  const creditScoreCriterion = getCriterion(TYPES_MAP.CREDIT_SCORE);
  const incomeToRentRatioCriterion = getCriterion(TYPES_MAP.INCOME_TO_RENT);

  const isCreditScoreEnabled = creditScoreCriterion.isEnabled ?? true;
  const isRentToIncomeRatioEnabled = incomeToRentRatioCriterion.isEnabled ?? true;

  const creditScoreAlert = creditScoreCriterion.alert ?? null;
  const rentToIncomeRatioAlert = incomeToRentRatioCriterion.alert ?? null;

  const incomeToRentRatioMax = incomeToRentRatioCriterion.max;

  const displayAlertContainer = isCreditScoreEnabled || isRentToIncomeRatioEnabled;

  // the fields that need to be saved successfully before we close the edit modal
  const successFields = [
    ...(isRentToIncomeRatioEnabled ? [TYPES_MAP.INCOME_TO_RENT] : []),
    ...(isCreditScoreEnabled ? [TYPES_MAP.CREDIT_SCORE] : []),
  ];

  const { mutateAsync: updateListingCriteria } = useSetListingCriteria();

  const { mutateAsync: updateMultiplexListingCriteria } = useMultiplexSetListingCriteria();

  useEffect(() => {
    if (isInModalState) {
      setSaveOutsideComponent();
    }
  }, []);

  useEffect(() => {
    if (isFetchingData || hasServerError()) {
      return;
    }

    if (checkIfAllFieldsHaveValues()) {
      saveDebounced();
    }
  }, [dataToBeSavedMap.values()]);

  const checkIfAllFieldsHaveValues = () => {
    return successFields.every((element) => Array.from(dataToBeSavedMap.keys()).includes(element));
  };

  const setSaveOutsideComponent = () => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        saveOutsideComponent: true,
      },
    });
  };

  const getDataToSave = () => {
    const dataList = [];

    for (const valueObj of dataToBeSavedMap.values()) {
      const { fieldName, value = null } = valueObj;

      // if there are no changes value will be null
      if (value !== null) {
        const criterionData = Criterion({
          ...value,
          isEnabled: true,
          type: fieldName,
        });

        dataList.push(criterionData);
      }
    }

    return dataList;
  };

  const getPartialMessagesFromAlertLinks = (links: Array<PartialMessageProps> = []) => {
    const response: Array<PartialMessageProps> = [];

    const isLastIndex = links?.length - 1;

    links?.forEach((link, index) => {
      response.push({
        isBold: link.isBold,
        text: link.text,
        url: link.url,
      });

      if (index !== isLastIndex) {
        // add space between links
        response.push({
          text: ' ',
        });
      }
    });

    return response;
  };

  const hasServerError = () => {
    return serverErrorObj.fieldName !== '';
  };

  /**
   * criterion can not be an empty string when saving
   * set to NOT_APPLICABLE if empty
   */
  const normalizeCriterion = (dataArray: Array<CriterionProps>) => {
    if (dataArray.length === 0) {
      return dataArray;
    }

    const dataArrayCopy = dataArray.slice(0);

    if (dataArrayCopy[0]['criterion'] === '') {
      dataArrayCopy[0]['criterion'] = NOT_APPLICABLE;
    }

    return dataArrayCopy;
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const onMutationHttpError = (err: { error: ErrorRMWProps }) => {
    const {
      error: { message },
    } = err;

    updateServerError(message, 'multi');
  };

  const saveMultiplexListingCriteria = async (dataArray: Array<CriterionProps>) => {
    /**
     * Defensive guard only: multiplex save runs when `isInMultiplexListingFlow` is true, which should
     * already imply a session id. `useMultiplexSetListingCriteria` also rejects empty ids before any request.
     */
    if (!multiplexBuildingSessionId) {
      logError({
        error: new Error('Unable to save: missing multiplexBuildingSessionId'),
        context: {
          details: 'FormSectionContainer.saveMultiplexListingCriteria',
          fieldName: 'updateMultiplexListingCriteria',
        },
      });

      return;
    }

    try {
      const response = await updateMultiplexListingCriteria({
        getParams: { multiplexBuildingSessionId },
        postData: {
          listingCriteria: dataArray.map(mapCriterionToMultiplexListingCriterionRequest),
        },
      });

      if (response?.success === false) {
        updateServerError(response.errorMessage ?? 'Unable to save listing criteria.', 'multi');
        return;
      }

      actions.clearFieldDataEntry({ reducerDispatch });
    } catch (err: unknown) {
      if (
        typeof err === 'object' &&
        err !== null &&
        'error' in err &&
        typeof (err as { error?: { message?: string } }).error === 'object'
      ) {
        onMutationHttpError(err as { error: ErrorRMWProps });
      } else {
        const message = DatarouterError.is(err)
          ? DatarouterError.getDisplayMessage(err)
          : ((err as { message?: string })?.message ?? '');

        updateServerError(message, 'multi');
      }
    }
  };

  const syncCriteriaToReduxAfterSave = (savedCriteriaDelta: Array<CriterionProps>) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        listingCriteria: ListingCriteria({
          isEnabled: true,
          criteria: [...criteriaForSection, ...savedCriteriaDelta],
        }),
      }),
    );

    actions.clearFieldDataEntry({ reducerDispatch });

    // reset save cta
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
  };

  const save = () => {
    const dataArray: Array<CriterionProps> = normalizeCriterion(getDataToSave());

    if (dataArray.length === 0) {
      actions.clearFieldDataEntry({ reducerDispatch });

      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    if (isOnReviewStep === false && isInMultiplexListingFlow) {
      return saveMultiplexListingCriteria(dataArray);
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    updateListingCriteria({
      getParams: { listingAlias },
      postData: dataArray as Array<UserPropertyCriteriaSetRequestWebDto>,
    })
      .then(() => {
        syncCriteriaToReduxAfterSave(dataArray);
      })
      .catch(onMutationHttpError)
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  return (
    <div data-testid="FormSectionContainer">
      {serverErrorObj.fieldName === 'multi' && (
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      )}

      <WizardFormSectionContainer isInMultiplexListingFlow={Boolean(isInMultiplexListingFlow)}>
        {isInMultiplexListingFlow === false && (
          <WizardHeading level={1} textStyle="heading-sm">
            What are your financial expectations for renters?
          </WizardHeading>
        )}

        <WizardBody>
          We display these criteria on your listing and use them to help you review prospective renters, so you can
          quickly see who’s a good fit for your property.
        </WizardBody>

        {displayAlertContainer && (
          <Box data-testid="FormSectionContainerAlert" css={{ marginBlock: '800' }}>
            {isCreditScoreEnabled && creditScoreAlert && (
              <Box data-testid="FormSectionContainerAlert-CreditScore" css={{ marginBottom: '400' }}>
                <Alert tone="warning">
                  <>
                    <Box css={{ marginBottom: '200' }}>{creditScoreAlert.body}</Box>

                    <PartialMessage
                      linkComponent={Anchor}
                      partialMessages={getPartialMessagesFromAlertLinks(creditScoreAlert?.links ?? [])}
                    />
                  </>
                </Alert>
              </Box>
            )}

            {isRentToIncomeRatioEnabled && rentToIncomeRatioAlert && (
              <Box data-testid="FormSectionContainerAlert-RentToIncomeRatio" css={{ marginBottom: '400' }}>
                <Alert tone="warning">
                  <>
                    <Box css={{ marginBottom: '200' }}>{rentToIncomeRatioAlert.body}</Box>

                    <PartialMessage
                      linkComponent={Anchor}
                      partialMessages={getPartialMessagesFromAlertLinks(rentToIncomeRatioAlert?.links ?? [])}
                    />
                  </>
                </Alert>
              </Box>
            )}
          </Box>
        )}

        {isRentToIncomeRatioEnabled && (
          <FieldSet>
            <IncomeToRentRatioFormField
              criteria={isInMultiplexListingFlow ? initialCriteria : undefined}
              label="Minimum income-to-rent ratio"
              incomeToRentRatioMax={incomeToRentRatioMax}
            />
          </FieldSet>
        )}

        {isCreditScoreEnabled && (
          <MinCreditScoreFormFieldContainer criteria={isInMultiplexListingFlow ? initialCriteria : undefined} />
        )}
      </WizardFormSectionContainer>
    </div>
  );
};

export default FormSectionContainer;
